import React, { useEffect, useMemo, useState } from 'react';
import {
  Card,
  Typography,
  Box,
  Grid,
  Button,
  Divider,
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import axios from 'axios';
import { KeyUri } from '../key';
import { PropertyStatusCards } from './Propertystatuscards';
const vendorToken = localStorage.getItem('vendorToken') || null;

const MAX_ACTIVITIES = 6;

// "2 hours ago", "3 days ago", ...
const timeAgo = (date) => {
  const diff = Date.now() - new Date(date).getTime();
  if (Number.isNaN(diff)) return '';
  const sec = Math.max(0, Math.floor(diff / 1000));
  if (sec < 60) return 'Just now';
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} minute${min > 1 ? 's' : ''} ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} hour${hr > 1 ? 's' : ''} ago`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day} day${day > 1 ? 's' : ''} ago`;
  const month = Math.floor(day / 30);
  if (month < 12) return `${month} month${month > 1 ? 's' : ''} ago`;
  const year = Math.floor(day / 365);
  return `${year} year${year > 1 ? 's' : ''} ago`;
};

// Turn the vendor's properties into a list of recent events, newest first.
// Uses only fields the /get-properties endpoint already returns.
const buildActivities = (properties) => {
  const events = [];

  properties.forEach((p) => {
    const name = p.propertyName || 'Untitled property';
    const updated = p.updatedAt || p.createdAt;

    // Main status of the property
    switch (p.status) {
      case 'approved':
        events.push({
          title: `${name} approved`,
          date: p.approvedAt || updated,
          status: 'success',
        });
        break;
      case 'pending':
        events.push({
          title: `${name} submitted for review`,
          date: p.submittedAt || updated,
          status: 'pending',
        });
        break;
      case 'rejected':
        events.push({
          title: `${name} rejected${p.rejectionReason ? `: ${p.rejectionReason}` : ''}`,
          date: updated,
          status: 'error',
        });
        break;
      case 'draft':
        events.push({
          title: `${name} saved as draft`,
          date: updated,
          status: 'info',
        });
        break;
      default:
        break;
    }

    // Edits made to a live property
    if (p.status === 'approved' && p.changeRequestStatus === 'pending') {
      events.push({
        title: `Changes to ${name} awaiting approval`,
        date: p.changesSubmittedAt || updated,
        status: 'pending',
      });
    }
    if (p.status === 'approved' && p.changeRequestStatus === 'rejected') {
      events.push({
        title: `Changes to ${name} rejected${p.rejectionReason ? `: ${p.rejectionReason}` : ''}`,
        date: updated,
        status: 'error',
      });
    }
  });

  return events
    .filter((e) => e.date && !Number.isNaN(new Date(e.date).getTime()))
    .sort((a, b) => new Date(b.date) - new Date(a.date))
    .slice(0, MAX_ACTIVITIES);
};

const Dashboard = ({ setActiveTab }) => {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProperties = async () => {
      if (!vendorToken) {
        setLoading(false);
        return;
      }
      try {
        const response = await axios.get(`${KeyUri.BACKEND_URI}/get-properties`, {
          headers: { Authorization: `Bearer ${vendorToken}` },
        });
        setProperties(response.data.data || []);
      } catch (error) {
        console.error('Error fetching properties:', error);
        setProperties([]);
      } finally {
        setLoading(false);
      }
    };
    fetchProperties();
  }, []);

  const recentActivities = useMemo(() => buildActivities(properties), [properties]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'success':
        return '#22C55E';
      case 'pending':
        return '#EAB308';
      case 'error':
        return '#EF4444';
      default:
        return '#3B82F6';
    }
  };

  if (loading) {
    return <Typography>Loading dashboard...</Typography>;
  }

  return (
    <Box sx={{ py: 3, px: { xs: 2, md: 4 } }}>

      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h2" gutterBottom color="text.primary" fontWeight={700}>
          Welcome Back!
        </Typography>
        <Typography color="text.secondary">
          Here's an overview of your properties and performance.
        </Typography>
      </Box>

      <Box sx={{ mb: 4 }}>
        <PropertyStatusCards properties={properties} showTotal />
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} lg={6}>
          <Card sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom fontWeight={700} color="text.primary">
              Recent Activity
            </Typography>

            {recentActivities.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No recent activity yet. Add a property to get started.
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {recentActivities.map((activity, index) => (
                  <Box
                    key={`${activity.title}-${index}`}
                    sx={{
                      display: 'flex',
                      gap: 2,
                      pb: 2,
                      borderBottom: index === recentActivities.length - 1 ? 'none' : '1px solid',
                      borderColor: 'divider',
                      alignItems: 'start',
                    }}
                  >
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        mt: '8px',
                        bgcolor: getStatusColor(activity.status),
                        flexShrink: 0,
                      }}
                    />
                    <Box>
                      <Typography variant="body2" color="text.primary">
                        {activity.title}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {timeAgo(activity.date)}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
          </Card>
        </Grid>

        <Grid item xs={12} lg={6}>
          <Card sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom fontWeight={700} color="text.primary">
              Quick Actions
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Button
                variant="outlined"
                startIcon={<BusinessIcon sx={{ color: '#2563EB' }} />}
                sx={{ justifyContent: 'flex-start', p: 2, textTransform: 'none' }}
                fullWidth
                onClick={() => setActiveTab('add-property')}
              >
                <Box textAlign="left">
                  <Typography variant="body1" color="text.primary">
                    Add New Property
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    List a new property on the platform
                  </Typography>
                </Box>
              </Button>

              {/*<Button
                variant="outlined"
                startIcon={<TrendingUpIcon sx={{ color: '#16A34A' }} />}
                sx={{ justifyContent: 'flex-start', p: 2, textTransform: 'none' }}
                fullWidth
                onClick={() => setActiveTab('analytics')}
              >
               <Box textAlign="left">
                  <Typography variant="body1" color="text.primary">
                    View Analytics
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Check your property performance
                  </Typography>
                </Box>
              </Button>*/}
            </Box>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;